import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UserCircle } from "@phosphor-icons/react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

function fmtErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Account() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({ institution: user?.institution || "", specialization: user?.specialization || "" });
  const [savingProfile, setSavingProfile] = useState(false);

  const inputCls = "w-full border border-white/15 bg-ink-900/60 px-4 py-3 font-body text-white outline-none transition-colors focus:border-cyan";
  const roCls = "w-full border border-white/10 bg-ink-900/30 px-4 py-3 font-body text-slate-400 cursor-not-allowed";
  const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success(t("password_changed"));
      setCurrent(""); setNext("");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.put("/auth/profile", profile);
      setUser((u) => ({ ...u, ...data }));
      toast.success(t("profile_saved"));
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div data-testid="account-page" className="relative min-h-screen px-6 pt-32 pb-28 md:px-10 grid-lines">
      <div className="mx-auto max-w-2xl">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="flex items-center gap-3">
            <UserCircle size={26} className="text-cyan" />
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan">— {t("account")}</p>
          </div>
          <h1 className="mt-4 font-head text-4xl font-black uppercase tracking-tighter text-white md:text-6xl">{t("my_account")}</h1>
          {user && <p className="mt-4 font-mono text-sm text-slate-400">{user.name} · {user.email}</p>}
        </motion.div>

        {/* Profile */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-10 border border-white/10 bg-ink-800/60 p-8 backdrop-blur-xl">
          <h2 className="mb-6 font-head text-xl font-bold uppercase tracking-tight text-white">{t("my_account")}</h2>
          <form onSubmit={saveProfile} className="flex flex-col gap-5">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div><label className={labelCls}>{t("last_name")}</label><input className={roCls} value={user?.last_name || ""} readOnly /></div>
              <div><label className={labelCls}>{t("first_name")}</label><input className={roCls} value={user?.first_name || ""} readOnly /></div>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div><label className={labelCls}>{t("birth_date")}</label><input className={roCls} value={user?.birth_date || ""} readOnly /></div>
              <div><label className={labelCls}>Email</label><input className={roCls} value={user?.email || ""} readOnly /></div>
            </div>
            <div>
              <label className={labelCls}>{t("institution")}</label>
              <input data-testid="acc-institution" className={inputCls} value={profile.institution} onChange={(e) => setProfile({ ...profile, institution: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>{t("specialization")}</label>
              <input data-testid="acc-specialization" className={inputCls} value={profile.specialization} onChange={(e) => setProfile({ ...profile, specialization: e.target.value })} />
            </div>
            <button data-testid="acc-profile-submit" type="submit" disabled={savingProfile} className="mt-2 w-fit bg-cyan px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink-900 transition-colors hover:bg-white disabled:opacity-50">
              {savingProfile ? "…" : t("save")}
            </button>
          </form>
        </motion.div>

        {/* Change password */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-8 border border-white/10 bg-ink-800/60 p-8 backdrop-blur-xl">
          <h2 className="mb-6 font-head text-xl font-bold uppercase tracking-tight text-white">{t("change_password")}</h2>
          <form onSubmit={submit} className="flex flex-col gap-5">
            <div>
              <label className={labelCls}>{t("current_password")}</label>
              <input data-testid="acc-current-password" type="password" required className={inputCls} value={current} onChange={(e) => setCurrent(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>{t("new_password")}</label>
              <input data-testid="acc-new-password" type="password" required minLength={6} className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} />
            </div>
            <button data-testid="acc-submit" type="submit" disabled={loading} className="mt-2 w-fit bg-cyan px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink-900 transition-colors hover:bg-white disabled:opacity-50">
              {loading ? "…" : t("update_password")}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
